import React, { useState } from 'react';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, X } from 'lucide-react';
import { requestPasswordReset } from '../../../services/forgetpassapi';
import { useTheme } from '../../Home/ThemeContext';

export default function ForgotPasswordPage() {
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      console.error('Forgot Password Error:', err);
      // Don't reveal if email exists for security
      setSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen pt-32 pb-12 px-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-md mx-auto">
        
        {/* Back Button */}
        <a
          href="/login"
          className={`inline-flex items-center gap-2 mb-6 text-sm font-semibold transition-colors ${
            isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </a>

        {/* Card */}
        <div className={`rounded-2xl shadow-2xl p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Forgot Password?
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No worries! Enter your email and we'll send you reset instructions.
            </p>
          </div>

          {/* Success Message */}
          {success ? (
            <div className="space-y-6">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold mb-1">Email Sent Successfully!</p>
                  <p className="text-sm">
                    If an account exists with <strong>{email}</strong>, you will receive password reset instructions shortly.
                  </p>
                </div>
              </div>

              <div className={`p-4 rounded-lg text-sm ${
                isDarkMode ? 'bg-blue-500/10 border border-blue-500/20 text-blue-300' : 'bg-blue-50 border border-blue-200 text-blue-700'
              }`}>
                <p className="font-semibold mb-2">📧 What's next?</p>
                <ul className="space-y-1 text-xs">
                  <li>• Check your email inbox</li>
                  <li>• Click the reset link (valid for 1 hour)</li>
                  <li>• Create your new password</li>
                  <li>• Check spam folder if you don't see it</li>
                </ul>
              </div>

              <button
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-3 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all"
              >
                Send Another Email
              </button>

              <a
                href="/login"
                className={`block text-center text-sm font-semibold transition-colors ${
                  isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Back to Login
              </a>
            </div>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm flex-1">{error}</p>
                  <button onClick={() => setError('')}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Form */}
              <div className="space-y-6">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={isLoading || !email}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-4 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>

                <p className={`text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Remember your password?{' '}
                  <a href="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                    Login
                  </a>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}